/**
 * My notifications: each person's own choice per category (and per event)
 * of immediately, a digest or off. Account mail always arrives and is not
 * listed. Opened from the account menu.
 */
import { useEffect, useState } from "react";
import { put, useApi } from "../../kit/api";
import { Banner, Button, Seg, Skeleton } from "../../kit/ui";
import { Dialog, useToast } from "../../kit/overlays";

type Topic = { key: string; label: string; events: { key: string; label: string; class: string }[] };
type Data = { topics: Topic[]; prefs: Record<string, string> };
type Mode = "default" | "immediate" | "digest" | "off";
const OPTIONS: [Mode, string][] = [["default", "As set"], ["immediate", "Immediately"], ["digest", "Digest"], ["off", "Off"]];

export function MyNotifications({ onClose }: { onClose: () => void }) {
  const { data, error } = useApi<Data>("/api/notifications/me");
  const toast = useToast();
  const [prefs, setPrefs] = useState<Record<string, Mode> | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (data && !prefs) setPrefs(data.prefs as Record<string, Mode>);
  }, [data, prefs]);
  const base = (data?.prefs ?? {}) as Record<string, Mode>;
  const keys = new Set([...Object.keys(base), ...Object.keys(prefs ?? {})]);
  const changes = Object.fromEntries([...keys].filter((k) => (prefs?.[k] ?? "default") !== (base[k] ?? "default")).map((k) => [k, prefs?.[k] === "default" || !prefs?.[k] ? null : prefs[k]]));
  const save = () => {
    setBusy(true);
    put<{ prefs: Record<string, string> }>("/api/notifications/me", { prefs: changes })
      .then(() => (toast("Your notification choices are saved."), onClose()))
      .catch((e: Error) => toast(e.message, "err"))
      .finally(() => setBusy(false));
  };
  const set = (k: string, v: Mode) => setPrefs((p) => ({ ...(p ?? {}), [k]: v }));
  const row = (k: string, label: string, sub?: string, nested = false) => (
    <div key={k} className="row" style={{ justifyContent: "space-between", gap: 12, padding: nested ? "6px 0 6px 18px" : "10px 0", borderTop: nested ? undefined : "1px solid var(--ln)" }}>
      <div className="grow" style={{ minWidth: 0 }}>
        <div style={{ fontWeight: nested ? 400 : 600 }}>{label}</div>
        {sub && <div className="faint" style={{ fontSize: 12.5 }}>{sub}</div>}
      </div>
      <Seg value={prefs?.[k] ?? "default"} onChange={(v) => set(k, v)} options={OPTIONS} />
    </div>
  );
  return (
    <Dialog open onClose={onClose} busy={busy} size="wide" icon="alert" title="My notifications" lead="How the Partner Portal reaches you. As set follows your partner's rules. Setup links, resets and other account mail always arrive."
      footer={<><Button onClick={onClose} disabled={busy}>Cancel</Button><Button kind="primary" busy={busy} disabled={!Object.keys(changes).length} onClick={save}>Save</Button></>}>
      {!data || !prefs ? (
        error ? <Banner tone="red" icon="error-circle">{error.message}</Banner> : <Skeleton rows={6} />
      ) : (
        <div className="col">
          {data.topics.map((t) => (
            <div key={t.key}>
              {row(t.key, t.label, `${t.events.length} ${t.events.length === 1 ? "kind" : "kinds"} of notice`)}
              <button type="button" className="linkbtn" style={{ fontSize: 12.5, marginBottom: 6 }} onClick={() => setOpen(open === t.key ? null : t.key)}>{open === t.key ? "Hide each notice" : "Choose for each notice"}</button>
              {open === t.key && t.events.map((e) => row(e.key, e.label, undefined, true))}
            </div>
          ))}
        </div>
      )}
    </Dialog>
  );
}
